import { CoreApplicationContext } from '@eb/context-gen';
import { setLanguage } from '@eb/i18n';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import React from 'react';
import { ErrorsPage } from './components/error/ErrorsPage';
import { WhoopsPage } from './components/whoops/WhoopsPage';
import {
    ACTION_RELOAD_CLICKED,
    CATEGORY_ONE_CHECKOUT,
    CTA_ACTION,
    RETRY_PARAM,
} from './constants';
import { Page } from './containers/page/Page';
import { EventbriteEvent } from './models';

export interface AppProps extends CoreApplicationContext {
    app_name: string;
    event?: EventbriteEvent;
    eventId: string;
    eventUrl: string;
    isModal: boolean;
    isEventbriteTLD: boolean;
    retryAttempts: number;
    useIosProfile: boolean;
    useAndroidProfile: boolean;
    widgetParentUrl: string;
    isUnavailableEvent: boolean;
    isValidHTTPSRequest: boolean;
}

export class App extends React.Component<AppProps & CoreApplicationContext> {
    constructor(props: AppProps & CoreApplicationContext) {
        super(props);
        setLanguage(props.env.localeInfo.locale);
    }

    _isNativeApp = () => {
        const { useIosProfile, useAndroidProfile } = this.props;
        return useIosProfile || useAndroidProfile;
    };

    _shouldShowModal = () => {
        const { isModal } = this.props;
        return isModal || this._isNativeApp();
    };

    _getRetryNumber = () => {
        const currentLoc = new URL(window.location.href);
        const retryNumber = currentLoc.searchParams.get(RETRY_PARAM) || '0';
        return parseInt(retryNumber) + 1;
    };

    _getRetryUrl = (retryNumber: number) => {
        const currentLoc = new URL(window.location.href);
        currentLoc.searchParams.set(RETRY_PARAM, retryNumber.toString());
        return currentLoc.href;
    };

    _onCTAClick = (action: CTA_ACTION) => {
        let url;
        let target;

        switch (action) {
            case CTA_ACTION.GO_TO_LISTING_PAGE: {
                const { eventUrl } = this.props;

                url = eventUrl;
                target = '_blank';
                break;
            }
            case CTA_ACTION.RELOAD:
            default: {
                const { app_name: label } = this.props;
                const { path: requestPath } = this.props.request;
                const retryNumber = this._getRetryNumber();

                trackAnalyticsEvent({
                    action: ACTION_RELOAD_CLICKED,
                    category: CATEGORY_ONE_CHECKOUT,
                    label,
                    requestPath,
                    value: retryNumber,
                });

                url = this._getRetryUrl(retryNumber);
                target = '_self';
                break;
            }
        }

        window.open(url, target);
    };

    render() {
        const {
            env,
            event,
            eventId,
            isEventbriteTLD,
            isUnavailableEvent,
            isValidHTTPSRequest,
            retryAttempts,
            widgetParentUrl,
        } = this.props;

        let view;

        if (!isValidHTTPSRequest || isUnavailableEvent) {
            view = (
                <ErrorsPage
                    isValidHTTPSRequest={isValidHTTPSRequest}
                    onCTAClick={this._onCTAClick}
                    env={env}
                    event={event}
                />
            );
        } else {
            view = (
                <WhoopsPage
                    isEventbriteTLD={isEventbriteTLD}
                    onCTAClick={this._onCTAClick}
                    retryAttempts={retryAttempts}
                />
            );
        }

        return (
            <Page
                data-spec="checkout-error-page"
                eventId={eventId}
                isNativeApp={this._isNativeApp()}
                showModal={this._shouldShowModal()}
                widgetParentUrl={widgetParentUrl}
            >
                {view}
            </Page>
        );
    }
}
